import { experiments, type ExperimentFlag } from "@/lib/experiments";

// Query-param experiment override, e.g. `?ab=hero-delay-banner:banner,reviews-google-snippet:green`.
// Lets the admin preview a specific variant combination without touching PostHog.
// Purely presentational (copy + which marketing element shows), so it is safe to
// honour on any request — there is nothing gated behind it.
export const AB_PARAM = "ab";

// Parse the `ab` value into a validated flag -> variant map, dropping unknown
// flags and variants that are not declared in the registry.
export function parseAbOverrides(search: string): Partial<Record<ExperimentFlag, string>> {
  const out: Partial<Record<ExperimentFlag, string>> = {};
  const raw = new URLSearchParams(search).get(AB_PARAM);
  if (!raw) return out;

  const registry = experiments as Record<string, { variants: readonly string[] }>;
  for (const pair of raw.split(",")) {
    const [flag, variant] = pair.split(":").map((s) => s?.trim());
    if (!flag || !variant) continue;
    if (registry[flag]?.variants.includes(variant)) {
      out[flag as ExperimentFlag] = variant;
    }
  }
  return out;
}

// Build the `ab` query value from a flag -> variant map. Empty values (= control,
// i.e. "no override") are dropped so the URL only carries real overrides.
export function buildAbParam(overrides: Record<string, string>): string {
  return Object.entries(overrides)
    .filter(([, variant]) => variant)
    .map(([flag, variant]) => `${flag}:${variant}`)
    .join(",");
}
